import { useEffect, useRef, useState } from 'react'
import { GoalTypePicker } from '@/components/custom/ogsm/objective-choice-controls'
import {
  FormBody,
  FormFooter,
  OgsmDialogContent,
  OgsmDialogHeader,
} from '@/components/custom/ogsm/ogsm-dialog-shell'
import { ScorecardDatePicker } from '@/components/custom/scorecard/scorecard-date-picker'
import { ScorecardTargetValueInput } from '@/components/custom/scorecard/scorecard-target-value-input'
import { Button } from '@/components/ui/button'
import { Dialog, DialogDescription } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Typography, typographyVariants } from '@/components/ui/typography'
import { useBoardEdit } from '@/features/ogsm/board-edit-context'
import { ogsmErrorMessage } from '@/features/ogsm/errors'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { toScorecardDataType } from '@/features/ogsm/target-format'
import { useOgsmApi } from '@/hooks/use-ogsm'
import { useReviseOgsmTargetMutation } from '@/hooks/use-ogsm-data'
import { cn } from '@/lib/utils'
import type { CreateOgsmRevisionInput, OgsmBoardNode } from '@/types/api'

type RevisionKind = CreateOgsmRevisionInput['kind']
type UnitType = NonNullable<CreateOgsmRevisionInput['targetType']>

const UNIT_TYPES: readonly UnitType[] = ['NUMBER', 'CURRENCY', 'PERCENT']
const isUnitType = (type: string): type is UnitType =>
  UNIT_TYPES.some((unit) => unit === type)

const KINDS: { kind: RevisionKind; label: string; note: string }[] = [
  {
    kind: 'CORRECTION',
    label: OGSM_COPY.correction,
    note: OGSM_COPY.correctionNote,
  },
  { kind: 'REVISION', label: OGSM_COPY.revision, note: OGSM_COPY.revisionNote },
]

function ReviseForm({
  node,
  isGoal,
  onDone,
}: {
  node: OgsmBoardNode
  isGoal: boolean
  onDone: () => void
}) {
  const { companyId, session } = useBoardEdit()
  const revise = useReviseOgsmTargetMutation(useOgsmApi(companyId), session.id)
  const [step, setStep] = useState<'edit' | 'choose'>('edit')
  const [pressed, setPressed] = useState<RevisionKind | null>(null)
  const [type, setType] = useState(node.targetType)
  const [value, setValue] = useState(node.targetValue ?? Number.NaN)
  const [text, setText] = useState(node.targetText ?? '')
  const [date, setDate] = useState(node.targetDate)
  const [descriptor, setDescriptor] = useState(node.descriptor ?? '')
  const [reason, setReason] = useState('')
  const isText = node.targetType === 'TEXT'
  const isDate = node.targetType === 'DATE'
  const isNumeric = !isText && !isDate
  const newLabel = isGoal ? OGSM_COPY.newGoal : OGSM_COPY.newMeasure
  const id = (field: string) => `revise-${field}-${node.id}`
  // A refetch can drop canCorrectType; the picked unit must drop with it.
  const unit = node.canCorrectType ? type : node.targetType
  const newUnit = isUnitType(unit) && unit !== node.targetType ? unit : null
  const unitChanged = newUnit !== null
  const newDescriptor = descriptor.trim() || null

  // Only what changed is sent: the backend refuses a revision that changes nothing.
  const input: Omit<CreateOgsmRevisionInput, 'kind'> = {
    ...(isNumeric && Number.isFinite(value) && value !== node.targetValue
      ? { newValue: value }
      : {}),
    ...(isText && text.trim() !== '' && text.trim() !== node.targetText
      ? { newText: text.trim() }
      : {}),
    ...(date !== node.targetDate ? { newDate: date } : {}),
    ...(unitChanged ? { targetType: newUnit } : {}),
    ...(unitChanged && newDescriptor !== node.descriptor
      ? { descriptor: newDescriptor }
      : {}),
    ...(reason.trim() !== '' ? { reason: reason.trim() } : {}),
  }
  const changed =
    input.newValue !== undefined ||
    input.newText !== undefined ||
    input.newDate !== undefined ||
    input.targetType !== undefined

  const dateField = (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id('date')}>
        {isDate ? newLabel : OGSM_COPY.targetDate}
      </Label>
      <ScorecardDatePicker id={id('date')} value={date} onChange={setDate} />
    </div>
  )

  const backRef = useRef<HTMLButtonElement>(null)
  const fieldsRef = useRef<HTMLDivElement>(null)
  const pressedRef = useRef<HTMLButtonElement>(null)
  const stepShown = useRef(step)

  // Choose opens on Back so a second Enter can't record either kind; Back returns to the first field.
  useEffect(() => {
    if (stepShown.current === step) return
    stepShown.current = step
    if (step === 'choose') backRef.current?.focus()
    else
      fieldsRef.current
        ?.querySelector<HTMLElement>('input, textarea, button')
        ?.focus()
  }, [step])

  // The pressed button is disabled while saving and drops focus; a retry gets it back.
  useEffect(() => {
    if (revise.isError) pressedRef.current?.focus()
  }, [revise.isError])

  return (
    <>
      <OgsmDialogHeader
        title={
          step === 'choose'
            ? OGSM_COPY.reviseChooseTitle
            : isGoal
              ? OGSM_COPY.reviseGoal
              : OGSM_COPY.reviseMeasure
        }
      >
        <DialogDescription
          className={cn(
            typographyVariants({ variant: 'body-sm' }),
            'text-primary/60'
          )}
        >
          {step === 'choose' ? OGSM_COPY.reviseHint : OGSM_COPY.reviseEditHint}
        </DialogDescription>
      </OgsmDialogHeader>
      <form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={(event) => {
          event.preventDefault()
          if (changed) setStep('choose')
        }}
      >
        {step === 'edit' ? (
          <FormBody>
            <div ref={fieldsRef} className="contents">
              {node.canCorrectType && (
                <div className="flex flex-col gap-2">
                  {/* The radiogroup below carries the same name as its aria-label. */}
                  <Label aria-hidden>{OGSM_COPY.unit}</Label>
                  <GoalTypePicker
                    types={UNIT_TYPES}
                    value={type}
                    rowLabels={false}
                    label={OGSM_COPY.unit}
                    description={OGSM_COPY.reviseTypeHint}
                    onChange={(next) => {
                      if (!isUnitType(next)) return
                      setType(next)
                      if (next === node.targetType)
                        setDescriptor(node.descriptor ?? '')
                    }}
                  />
                </div>
              )}
              {unitChanged && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor={id('descriptor')}>
                    {OGSM_COPY.unitWords}
                  </Label>
                  <Input
                    id={id('descriptor')}
                    value={descriptor}
                    maxLength={255}
                    onChange={(event) => setDescriptor(event.target.value)}
                  />
                </div>
              )}
              {isNumeric && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor={id('value')}>{newLabel}</Label>
                  <ScorecardTargetValueInput
                    id={id('value')}
                    value={value}
                    onChange={setValue}
                    dataType={toScorecardDataType(unit)}
                    unitLabel={
                      unit === 'NUMBER'
                        ? unitChanged
                          ? newDescriptor
                          : node.descriptor
                        : null
                    }
                  />
                </div>
              )}
              {isText && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor={id('text')}>{newLabel}</Label>
                  <Input
                    id={id('text')}
                    value={text}
                    maxLength={500}
                    onChange={(event) => setText(event.target.value)}
                  />
                </div>
              )}
              {dateField}
              <div className="flex flex-col gap-2">
                <Label htmlFor={id('reason')}>{OGSM_COPY.reason}</Label>
                <Textarea
                  id={id('reason')}
                  rows={2}
                  maxLength={2000}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </div>
            </div>
          </FormBody>
        ) : (
          <FormBody>
            {KINDS.map(({ kind, label, note }) => (
              <div key={kind} className="flex flex-col gap-1">
                <Typography variant="body" className="font-semibold">
                  {label}
                </Typography>
                <Typography variant="body-sm" className="text-primary/60">
                  {kind === 'REVISION' && input.targetType !== undefined
                    ? OGSM_COPY.reviseTypeNeedsCorrection
                    : note}
                </Typography>
              </div>
            ))}
            {revise.isError && (
              <Typography
                variant="body-sm"
                className="text-destructive"
                role="alert"
              >
                {ogsmErrorMessage(revise.error)}
              </Typography>
            )}
          </FormBody>
        )}

        <FormFooter>
          {step === 'edit' ? (
            <>
              <Button type="button" variant="outline" onClick={onDone}>
                {OGSM_COPY.cancel}
              </Button>
              <Button type="submit" disabled={!changed}>
                {OGSM_COPY.save}
              </Button>
            </>
          ) : (
            <>
              <Button
                ref={backRef}
                type="button"
                variant="outline"
                disabled={revise.isPending}
                onClick={() => {
                  revise.reset()
                  setStep('edit')
                }}
              >
                {OGSM_COPY.back}
              </Button>
              {KINDS.map(({ kind, label }) => (
                <Button
                  key={kind}
                  ref={pressed === kind ? pressedRef : undefined}
                  type="button"
                  disabled={
                    revise.isPending ||
                    (kind === 'REVISION' && input.targetType !== undefined)
                  }
                  onClick={() => {
                    setPressed(kind)
                    revise.mutate(
                      { nodeId: node.id, input: { ...input, kind } },
                      { onSuccess: onDone }
                    )
                  }}
                >
                  {revise.isPending && pressed === kind
                    ? OGSM_COPY.saving
                    : label}
                </Button>
              ))}
            </>
          )}
        </FormFooter>
      </form>
    </>
  )
}

/**
 * Changes a goal (objective) or measure (strategy). Save then asks whether it is
 * a revision, which keeps the original beneath it, or a correction, which
 * replaces it; nothing is pre-selected. Opened from the
 * node menu, a double-click on the target or the Edit objective dialog, all only on an editable session.
 */
export function ReviseTargetDialog({
  node,
  open,
  onOpenChange,
}: {
  node: OgsmBoardNode
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const isGoal = node.depth === 0
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <OgsmDialogContent>
        <ReviseForm
          node={node}
          isGoal={isGoal}
          onDone={() => onOpenChange(false)}
        />
      </OgsmDialogContent>
    </Dialog>
  )
}
